import React, { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Building, Mail, Phone, MapPin, Scale } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Imprint = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-sm border-b border-gray-200 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3 hover:scale-105 transition-all duration-300 cursor-pointer group">
            <img 
              src="/Favicon/favicon-196x196.png" 
              alt="KaufPause Logo" 
              className="w-8 h-8 sm:w-10 sm:h-10 -translate-y-0.5"
            />
            <span className="text-2xl font-bold text-gray-900">
              Kauf<span className="bg-gradient-to-r from-emerald-500 via-emerald-600 to-emerald-700 bg-clip-text text-transparent group-hover:from-emerald-400 group-hover:via-emerald-500 group-hover:to-emerald-600 transition-all duration-300">Pause</span>
            </span>
          </div>
          <button 
            onClick={() => navigate('/')} 
            className="text-gray-600 hover:text-emerald-600 transition-colors"
          >
            ← Zurück zur Startseite
          </button>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-16 mt-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Building className="w-8 h-8 text-emerald-600" />
              <h1 className="text-4xl font-bold text-gray-900">Impressum</h1>
            </div>
            <p className="text-xl text-gray-600">
              Rechtliche Informationen zu KaufPause
            </p>
          </div>

          <div className="space-y-8">
            <Card className="border-emerald-200 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-emerald-800">
                  <Building className="w-5 h-5" />
                  Angaben gemäß § 5 TMG
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Betreiber:</h4>
                    <div className="space-y-2 text-gray-700">
                      <p><strong>Tobias Poczesniok</strong></p>
                      <p>Einzelunternehmer</p>
                      <p>KaufPause</p>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Anschrift:</h4>
                    <div className="space-y-2 text-gray-700">
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 mt-1 text-emerald-600 flex-shrink-0" />
                        <div>
                          <p>Meißnergasse 7</p>
                          <p>1220 Wien</p>
                          <p>Österreich</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-blue-200 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-800">
                  <Mail className="w-5 h-5" />
                  Kontakt
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-1 gap-8">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">E-Mail:</h4>
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-blue-600" />
                      <a href="mailto:kontakt@kaufpause.at" className="text-blue-600 hover:underline">
                        kontakt@kaufpause.at
                      </a>
                    </div>
                  </div>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-blue-800 text-sm">
                    <strong>Hinweis:</strong> Wir antworten in der Regel innerhalb von 24 Stunden auf alle Anfragen.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-purple-200 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-purple-800">
                  <Scale className="w-5 h-5" />
                  Rechtliche Hinweise
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">GISA-Zahl:</h4>
                    <p className="text-gray-700">35206927</p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Gewerbeordnung:</h4>
                    <p className="text-gray-700">
                      <a href="https://www.ris.bka.gv.at" target="_blank" rel="noopener noreferrer" 
                         className="text-purple-600 hover:underline">
                        www.ris.bka.gv.at
                      </a>
                    </p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Aufsichtsbehörde:</h4>
                    <p className="text-gray-700">Magistratisches Bezirksamt für den 22. Bezirk</p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Mitgliedschaft:</h4>
                    <p className="text-gray-700">Mitglied der WKO – Multimedia Agentur</p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Verantwortlich für den Inhalt:</h4>
                    <p className="text-gray-700">Tobias Poczesniok (Anschrift wie oben)</p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Streitschlichtung:</h4>
                    <p className="text-gray-700 text-sm">
                      Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit: 
                      <a href="https://ec.europa.eu/consumers/odr/" target="_blank" rel="noopener noreferrer" 
                         className="text-purple-600 hover:underline ml-1">
                        https://ec.europa.eu/consumers/odr/
                      </a>
                    </p>
                    <p className="text-gray-700 text-sm mt-2">
                      Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer 
                      Verbraucherschlichtungsstelle teilzunehmen.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-orange-200 shadow-lg">
              <CardHeader>
                <CardTitle className="text-orange-800">Haftungsausschluss</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Haftung für Inhalte:</h4>
                    <p className="text-gray-700 text-sm">
                      Als Diensteanbieter sind wir gemäß den geltenden österreichischen Gesetzen für eigene Inhalte auf diesen Seiten 
                      nach den allgemeinen Gesetzen verantwortlich. Wir sind als 
                      Diensteanbieter jedoch nicht unter der Verpflichtung, übermittelte oder gespeicherte 
                      fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine 
                      rechtswidrige Tätigkeit hinweisen.
                    </p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Haftung für Links:</h4>
                    <p className="text-gray-700 text-sm">
                      Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen 
                      Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. 
                      Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber 
                      der Seiten verantwortlich.
                    </p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Urheberrecht:</h4>
                    <p className="text-gray-700 text-sm">
                      Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen 
                      dem österreichischen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art 
                      der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen 
                      Zustimmung des jeweiligen Autors bzw. Erstellers.
                    </p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Keine Gesundheits- oder Finanzberatung:</h4>
                    <p className="text-gray-700 text-sm">
                      KaufPause ist eine App zur Unterstützung bewusster Kaufentscheidungen und stellt keine 
                      medizinische, psychologische, therapeutische oder professionelle Finanzberatung dar. 
                      Bei gesundheitlichen oder schwerwiegenden finanziellen Problemen wenden Sie sich bitte 
                      an entsprechende Fachkräfte oder Beratungsstellen.
                    </p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Inhalte und Drittanbieter:</h4>
                    <p className="text-gray-700 text-sm">
                      Inhalte können unter Verwendung verschiedener Hilfsmittel erstellt werden. Alle 
                      veröffentlichten Inhalte werden redaktionell geprüft. Erwähnte Drittanbieter-Tools 
                      oder -Services stellen keine aktive Empfehlung dar. Wir übernehmen keine Haftung 
                      für externe Anwendungen und erhalten keine Provisionen oder Vergütungen für deren 
                      Erwähnung. Nutzerbeispiele dienen der Veranschaulichung und können fiktiv sein.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-gray-200 shadow-lg">
              <CardHeader>
                <CardTitle className="text-gray-800">Weitere Informationen</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700 text-sm">
                    <strong>Letzte Aktualisierung:</strong> {new Date().toLocaleDateString('de-DE')}<br/>
                    <strong>Website:</strong> <a href="https://kaufpause.at" className="text-emerald-600 hover:underline">kaufpause.at</a><br/>
                    <strong>Hosting:</strong> Helloly
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Imprint; 